import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Send, Check, X, Webhook as WebhookIcon, Activity } from "lucide-react";
import { api, formatApiError } from "../lib/api";

/**
 * Webhooks settings section — admin/manager only.
 * Allows configuring outbound webhook endpoints that fire on key business events
 * (quote sent, customer approval, install start, project goes live, …).
 */
export default function WebhooksSection() {
  const [hooks, setHooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [deliveries, setDeliveries] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [h, e] = await Promise.all([
        api.get("/webhooks"),
        api.get("/webhooks/events"),
      ]);
      setHooks(h.data || []);
      setEvents(e.data?.events || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (h) => {
    if (!window.confirm(`Delete webhook "${h.name}"? Any unsent deliveries will be discarded.`)) return;
    setBusyId(h.id);
    try {
      await api.delete(`/webhooks/${h.id}`);
      toast.success("Webhook deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusyId(null); }
  };

  const onTest = async (h) => {
    setBusyId(h.id);
    try {
      const { data } = await api.post(`/webhooks/${h.id}/test`);
      if (data.last_status === "ok") {
        toast.success(`Test ping delivered (${data.last_status_code || "200"})`);
      } else {
        toast.error(`Test ping failed: ${data.last_error || data.last_status_code || "unknown"}`);
      }
      setHooks((arr) => arr.map((x) => (x.id === h.id ? data : x)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusyId(null); }
  };

  const onToggleActive = async (h) => {
    try {
      const { data } = await api.patch(`/webhooks/${h.id}`, { active: !h.active });
      setHooks((arr) => arr.map((x) => (x.id === h.id ? data : x)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const onExpand = async (h) => {
    if (expandedId === h.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(h.id);
    try {
      const { data } = await api.get(`/webhooks/${h.id}/deliveries`);
      setDeliveries((d) => ({ ...d, [h.id]: data || [] }));
    } catch {
      setDeliveries((d) => ({ ...d, [h.id]: [] }));
    }
  };

  return (
    <section className="swiss-card-flat p-5 mt-6" data-testid="webhooks-section">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="label flex items-center gap-1">
            <WebhookIcon size={11} /> Integrations
          </div>
          <h2 className="font-display text-xl font-semibold mt-1">Webhooks</h2>
          <p className="muted text-xs mt-1 max-w-xl">
            Fire HMAC-signed JSON POSTs to your CRM, Slack, Zapier or n8n when a quote is sent, a customer
            signs, install starts or a site goes live. Requests carry an{" "}
            <code className="font-mono">X-Flow-Signature</code> header (sha256={"<hex>"}).
          </p>
        </div>
        <button
          className="btn-accent"
          onClick={() => setAdding(true)}
          data-testid="webhook-add-btn"
        >
          <Plus size={13} /> New webhook
        </button>
      </div>

      {loading && <div className="muted text-xs">Loading webhooks…</div>}

      {!loading && hooks.length === 0 && !adding && (
        <div className="muted text-xs py-4 text-center" data-testid="webhooks-empty">
          No webhooks configured yet. Click &ldquo;New webhook&rdquo; to add one.
        </div>
      )}

      {(adding || hooks.length > 0) && !loading && (
        <table className="data-table" data-testid="webhooks-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>URL</th>
              <th>Events</th>
              <th>Active</th>
              <th>Last delivery</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {adding && (
              <WebhookEditRow
                events={events}
                onCancel={() => setAdding(false)}
                onSaved={() => { setAdding(false); load(); }}
              />
            )}
            {hooks.map((h) => (
              <React.Fragment key={h.id}>
                <tr data-testid={`webhook-row-${h.id}`}>
                  <td className="font-medium">{h.name}</td>
                  <td className="font-mono text-[11px] max-w-[280px] truncate" title={h.url}>{h.url}</td>
                  <td className="text-[11px]">
                    <div className="flex flex-wrap gap-1">
                      {(h.events || []).map((ev) => (
                        <span
                          key={ev}
                          className="px-1.5 py-0.5 rounded bg-[var(--surface-2)] font-mono text-[10px]"
                        >
                          {ev}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <button
                      onClick={() => onToggleActive(h)}
                      className={`tag ${h.active ? "ok" : "ink"}`}
                      data-testid={`webhook-toggle-${h.id}`}
                      title={h.active ? "Click to pause" : "Click to enable"}
                    >
                      {h.active ? "active" : "paused"}
                    </button>
                  </td>
                  <td className="text-[11px]">
                    {h.last_delivered_at ? (
                      <button
                        onClick={() => onExpand(h)}
                        className="underline muted"
                        data-testid={`webhook-deliveries-${h.id}`}
                      >
                        <Activity size={10} className="inline mr-0.5" />
                        {h.last_status === "ok" ? (
                          <span style={{ color: "var(--ok)" }}>{h.last_status_code} OK</span>
                        ) : (
                          <span style={{ color: "var(--err)" }}>{h.last_status_code || "ERR"}</span>
                        )}
                        <span className="ml-1 font-mono text-[10px]">
                          {new Date(h.last_delivered_at).toLocaleString("en-GB")}
                        </span>
                      </button>
                    ) : (
                      <span className="muted">never</span>
                    )}
                  </td>
                  <td className="num space-x-1.5 whitespace-nowrap">
                    <button
                      onClick={() => onTest(h)}
                      disabled={busyId === h.id}
                      className="btn-ghost !py-1 !px-2 !text-[10px]"
                      data-testid={`webhook-test-${h.id}`}
                      title="Send a test.ping"
                    >
                      <Send size={11} /> Test
                    </button>
                    <button
                      onClick={() => onDelete(h)}
                      disabled={busyId === h.id}
                      data-testid={`webhook-del-${h.id}`}
                      title="Delete webhook"
                    >
                      <Trash2 size={13} className="muted" />
                    </button>
                  </td>
                </tr>
                {expandedId === h.id && (
                  <tr data-testid={`webhook-deliveries-row-${h.id}`}>
                    <td colSpan={6} className="bg-[var(--surface)] p-4">
                      <DeliveriesList items={deliveries[h.id] || []} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function WebhookEditRow({ events, onCancel, onSaved }) {
  const [form, setForm] = useState({ name: "", url: "", secret: "", events: [] });
  const [busy, setBusy] = useState(false);

  const toggleEvent = (ev) =>
    setForm((f) => ({
      ...f,
      events: f.events.includes(ev) ? f.events.filter((x) => x !== ev) : [...f.events, ev],
    }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim() || !form.url.trim() || form.events.length === 0) {
      toast.error("Name, URL and at least one event are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/webhooks", {
        name: form.name.trim(),
        url: form.url.trim(),
        secret: form.secret.trim() || null,
        events: form.events,
        active: true,
      });
      toast.success("Webhook created");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <tr data-testid="webhook-edit-row" className="bg-[var(--accent-soft)]/40">
      <td colSpan={6} className="p-4">
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
          <label className="md:col-span-3 block">
            <span className="label block mb-1">Name *</span>
            <input
              required
              className="input-swiss text-xs"
              placeholder="Slack — sales channel"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="webhook-name-input"
            />
          </label>
          <label className="md:col-span-5 block">
            <span className="label block mb-1">URL *</span>
            <input
              required
              type="url"
              className="input-swiss text-xs font-mono"
              placeholder="https://hooks.slack.com/services/T00/B00/abc"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              data-testid="webhook-url-input"
            />
          </label>
          <label className="md:col-span-4 block">
            <span className="label block mb-1">
              Signing secret <span className="muted">(optional)</span>
            </span>
            <input
              type="text"
              className="input-swiss text-xs font-mono"
              placeholder="Used to sign X-Flow-Signature header"
              value={form.secret}
              onChange={(e) => setForm({ ...form, secret: e.target.value })}
              data-testid="webhook-secret-input"
            />
          </label>
          <div className="md:col-span-12">
            <div className="label mb-1">Events *</div>
            <div className="flex flex-wrap gap-2" data-testid="webhook-events-picker">
              {events.map((ev) => {
                const on = form.events.includes(ev);
                return (
                  <button
                    type="button"
                    key={ev}
                    onClick={() => toggleEvent(ev)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-mono border transition-colors ${
                      on
                        ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                        : "bg-white text-[var(--ink-2)] border-[var(--line)] hover:border-[var(--accent)]"
                    }`}
                    data-testid={`webhook-event-${ev}`}
                  >
                    {on && <Check size={10} className="inline mr-1" />}
                    {ev}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="md:col-span-12 flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onCancel}
              className="btn-ghost text-xs"
              data-testid="webhook-cancel-btn"
            >
              <X size={11} /> Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="btn-accent text-xs"
              data-testid="webhook-save-btn"
            >
              {busy ? "Saving…" : "Create webhook"}
            </button>
          </div>
        </form>
      </td>
    </tr>
  );
}

function DeliveriesList({ items }) {
  if (!items.length) {
    return <div className="muted text-xs text-center py-3">No deliveries yet for this webhook.</div>;
  }
  return (
    <div data-testid="webhook-deliveries-list">
      <div className="label mb-2">Recent deliveries (last {items.length})</div>
      <table className="data-table">
        <thead>
          <tr>
            <th>When</th>
            <th>Event</th>
            <th>Status</th>
            <th>Error</th>
          </tr>
        </thead>
        <tbody>
          {items.map((d) => (
            <tr key={d.id}>
              <td className="font-mono text-[11px]">
                {new Date(d.created_at).toLocaleString("en-GB")}
              </td>
              <td className="font-mono text-[11px]">{d.event}</td>
              <td>
                <span
                  className="tag"
                  style={{
                    background: d.ok ? "var(--accent-soft)" : "#FEE2E2",
                    color: d.ok ? "var(--accent-hover)" : "#991B1B",
                  }}
                >
                  {d.status_code ?? "—"}
                </span>
              </td>
              <td className="text-[11px] max-w-[400px] truncate" title={d.error || ""}>
                {d.error || <span className="muted">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
